import React from "react";
import { Link } from "react-router-dom";
import { Play, Pause, Loader2, Globe, Sparkles } from "lucide-react";
import { usePlayer } from "@/context/PlayerContext";
import ShortlistButton from "@/components/ShortlistButton";
import Waveform from "@/components/Waveform";
import { mediaUrl } from "@/lib/api";
import { profileImage } from "@/lib/images";
import { langNL, countryNL, tagNL } from "@/config/languageLabels";
import { talentPath } from "@/config/childVoices";

export default function VoiceCard({ talent }) {
  const player = usePlayer();
  const demo = (talent.demos || [])[0];
  const trackKey = demo ? `${talent.id}:${demo.id}` : null;
  const active = trackKey && player.isActive(trackKey);
  const isPlaying = active && player.playing;
  const ageLabel = talent.age_min === talent.age_max ? `Leeftijd ${talent.age_min}` : `Leeftijd ${talent.age_min}–${talent.age_max}`;

  const onPlay = (e) => {
    e.preventDefault();
    if (!demo) return;
    player.playTrack({
      key: trackKey, name: talent.name, url: mediaUrl(demo.url),
      sub: `${langNL(talent.language)} · ${countryNL(talent.country)}`, photo: profileImage(talent),
      talentId: talent.id,
    });
  };

  return (
    <div data-testid={`voice-card-${talent.id}`}
      className="group relative flex flex-col rounded-2xl border border-zinc-200 bg-white p-4 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-zinc-200/60">
      <div className="flex items-start gap-4">
        <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-zinc-100">
          <img src={profileImage(talent)} alt={`${talent.name} — kinderstemacteur`}
            loading="lazy" className="h-full w-full object-cover" />
          {talent.featured && (
            <span className="absolute left-1 top-1 rounded-full bg-brand-violet px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">★</span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <Link to={talentPath(talent)} className="font-heading text-lg font-bold text-zinc-900 hover:text-brand-violet truncate">
              {talent.name}
            </Link>
            <ShortlistButton talent={talent} />
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm text-zinc-500">
            <span>{langNL(talent.language)}</span>
            <span className="text-zinc-300">·</span>
            <span>{ageLabel}</span>
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {talent.voice_type === "ai" && (
              <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-[11px] font-semibold text-brand-violet">
                <Sparkles size={11} /> AI-stem
              </span>
            )}
            {(talent.tags || []).slice(0, 2).map((t) => (
              <span key={t} className="rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] font-medium text-zinc-600">{tagNL(t)}</span>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <button onClick={onPlay} disabled={!demo} data-testid={`play-${talent.id}`}
          aria-label={isPlaying ? "Demo pauzeren" : "Demo afspelen"}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-violet text-white shadow-lg shadow-violet-500/25 transition hover:bg-brand-violetHover disabled:opacity-30 focus:outline-none focus:ring-2 focus:ring-brand-violet focus:ring-offset-2">
          {active && player.loading ? <Loader2 size={18} className="animate-spin" />
            : isPlaying ? <Pause size={18} className="fill-white" /> : <Play size={18} className="fill-white ml-0.5" />}
        </button>
        <Waveform seed={talent.id} count={40} height={34} className="flex-1"
          active={active} progress={active && player.duration ? player.progress / player.duration : 0}
          onSeek={active ? player.seek : undefined} />
      </div>
    </div>
  );
}
